import { SETTINGS_HUB_HASH_ROUTES, SETTINGS_HUB_SECTION_ROUTES } from '../../pages/settings/routes.ts';

/**
 * Where each Settings section lives, in both shapes of the page.
 *
 * With `settingsHub` off, Settings is one long page and a section is
 * an anchor on it: `/settings#settings-<id>`. With it on, `/settings` is a
 * hub of links and each section is a page of its own: `/settings/<slug>`.
 * Every link ever minted (emails, notifications, bookmarks, the mailbox
 * return's `#integrations`) uses the first form, and new links may use
 * either, so each shape redirects the other's URLs to itself. These helpers
 * are that translation, kept free of React so the router, the pages and the
 * links all agree on it.
 */

/** Every section id, as the `#settings-<id>` anchors have always named them. */
export const SETTINGS_SECTION_IDS = [
  'company',
  'pricing',
  'numbering',
  'library',
  'roomTypes',
  'layouts',
  'checklists',
  'locations',
  'hours',
  'recurringHorizon',
  'calendar',
  'reminders',
  'lateCancel',
  'fillSlots',
  'followups',
  'reengagement',
  'notifications',
  'messaging',
  'texting',
  'calls',
  'jobChat',
  'ai',
  'widget',
  'photoQuotes',
  'payments',
  'paymentMethods',
  'tips',
  'billing',
  'team',
  'teamPay',
  'aiAssistants',
  'apiKeys',
  'apiDocs',
  'account',
  'security',
  'referrals',
  'contact',
  'data',
  'customerImport',
  'logs',
  'delete',
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTION_IDS)[number];

export function isSettingsSectionId(value: string): value is SettingsSectionId {
  return (SETTINGS_SECTION_IDS as readonly string[]).includes(value);
}

/**
 * Sections that are pages of their own in both shapes of Settings. Pricing &
 * quotes is a hub at /settings/pricing with a page per policy, quote and
 * invoice numbering among them; texting & calls is a hub at
 * /settings/messaging over texting setup, calls and job chat. Their old
 * `#settings-<id>` hashes and their `/settings/<id>` addresses open these
 * pages, on the long page as on the hub.
 */
export const SETTINGS_SECTION_PAGES: Readonly<Record<string, string>> = {
  pricing: '/settings/pricing',
  numbering: '/settings/pricing/numbering',
  messaging: '/settings/messaging',
  texting: '/settings/texting',
  calls: '/settings/calls',
  jobChat: '/settings/job-chat',
};

/**
 * Every section that has a page of its own on the hub: the pages above, and
 * the child pages FEATURE_SETTINGS_HUB_ROUTES links the long page to
 * (templates, team, team pay, account, security, integrations — routed
 * whatever that flag says, pages/settings/routes.ts). On the hub those are
 * always where a section lives. Connected accounts is a page of the account
 * split rather than a section; sign-in returns land there.
 */
export const SETTINGS_HUB_PAGES: Readonly<Record<string, string>> = {
  ...SETTINGS_SECTION_PAGES,
  ...SETTINGS_HUB_SECTION_ROUTES,
  ...Object.fromEntries(
    Object.entries(SETTINGS_HUB_HASH_ROUTES).map(([hash, page]) => [hash.replace(/^#(settings-)?/, ''), page]),
  ),
  connectedAccounts: '/settings/connected-accounts',
  logs: '/logs',
  // One way into the docs: the reference page that has always been
  // /settings/api, rather than a second page holding the card that links to it.
  apiDocs: '/settings/api',
  customerImport: '/customers/import',
};

/**
 * The section whose `/settings/<slug>` page a section is: itself, or null
 * when it has a page of its own instead (SETTINGS_HUB_PAGES) or is not a section.
 */
export function settingsSectionOwner(id: string): SettingsSectionId | null {
  if (Object.hasOwn(SETTINGS_HUB_PAGES, id)) return null;
  return isSettingsSectionId(id) ? id : null;
}

/** `paymentMethods` → `payment-methods`: the URL segment for a section. */
export function settingsSectionSlug(id: SettingsSectionId): string {
  return id.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

/** `payment-methods` (or `paymentMethods`) → `paymentMethods`; null for anything else. */
export function settingsSectionIdFromSlug(slug: string): SettingsSectionId | null {
  const id = slug.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
  return isSettingsSectionId(id) ? id : null;
}

/**
 * Where a section opens on the hub: its own page (SETTINGS_HUB_PAGES), or
 * `/settings/<slug>`. `search` is carried over verbatim (`?plan=…`,
 * `?stripeConnect=…`).
 */
export function settingsLeafPath(id: string, search = ''): string {
  if (Object.hasOwn(SETTINGS_HUB_PAGES, id)) return `${SETTINGS_HUB_PAGES[id]}${search}`;
  if (!isSettingsSectionId(id)) return `/settings${search}`;
  return `/settings/${settingsSectionSlug(id)}${search}`;
}

/**
 * The same section on the long page: its page when it is one in both shapes,
 * else `/settings#settings-<id>` (or `#integrations`), which the long page
 * scrolls to or forwards to a child page as FEATURE_SETTINGS_HUB_ROUTES says.
 */
export function settingsLegacyPath(id: string, search = ''): string {
  if (Object.hasOwn(SETTINGS_SECTION_PAGES, id)) return `${SETTINGS_SECTION_PAGES[id]}${search}`;
  return id === 'integrations' ? `/settings${search}#integrations` : `/settings${search}#settings-${id}`;
}

/** Where a link to a section should point in this company's Settings shape. */
export function settingsSectionHref(id: string, hubRoutes: boolean, search = ''): string {
  return hubRoutes ? settingsLeafPath(id, search) : settingsLegacyPath(id, search);
}

/**
 * The section a Settings hash names — `#settings-<id>`, or `#integrations`,
 * the one link minted outside that scheme — or null when it is not one of
 * ours.
 */
export function settingsSectionFromHash(hash: string): string | null {
  if (hash === '#integrations') return 'integrations';
  const match = /^#settings-([A-Za-z]+)$/.exec(hash);
  if (!match?.[1]) return null;
  return isSettingsSectionId(match[1]) ? match[1] : null;
}

/**
 * Returns from outside the app that name no section, only a query the
 * section's own card reads: Stripe Checkout (`?billing=subscribed`, which
 * `BillingSection` polls on), signing in to link a provider
 * (`?oauth_linked=` / `?oauth_error=`, read by the connected-accounts card),
 * connecting a mailbox, Stripe Connect and Twilio Connect. On the long page
 * every card was mounted, so `/settings?…` was enough; on the hub the query
 * has to be taken to the page whose card reads it.
 */
const RETURN_PARAMS: ReadonlyArray<[param: string, section: string]> = [
  ['billing', 'billing'],
  ['oauth_linked', 'connectedAccounts'],
  ['oauth_error', 'connectedAccounts'],
  ['integration_connected', 'integrations'],
  ['integration_error', 'integrations'],
  ['stripeConnect', 'payments'],
  // Only the texting card words a Twilio Connect result for a person.
  ['twilioConnect', 'texting'],
];

/**
 * Where `/settings` with this hash and query belongs on the hub, or null to
 * stay on the hub. The Twilio Connect callback still returns to
 * `#settings-messaging` with a `?twilioConnect=` flag that only the texting
 * card words for a person, so that return goes straight to texting.
 */
export function settingsHubRedirect(hash: string, search: string): string | null {
  if (hash === '#settings-messaging' && new URLSearchParams(search).has('twilioConnect')) {
    return settingsLeafPath('texting', search);
  }
  const target = settingsSectionFromHash(hash) ?? settingsSectionFromReturn(search);
  return target ? settingsLeafPath(target, search) : null;
}

/** The section a bare `/settings?…` return belongs to, or null. */
export function settingsSectionFromReturn(search: string): string | null {
  const params = new URLSearchParams(search);
  return RETURN_PARAMS.find(([param]) => params.has(param))?.[1] ?? null;
}

/**
 * Where a `/settings/<slug>` URL belongs on the long page, for a company
 * whose Settings is still one page. A hash the leaf URL carried (a nested
 * anchor such as `#integrations`) wins over the section's own anchor.
 * Unknown slugs go to the top of Settings.
 */
export function settingsLegacyPathForSlug(slug: string, search = '', hash = ''): string {
  const fromHash = settingsSectionFromHash(hash);
  if (fromHash) return settingsLegacyPath(fromHash, search);
  const id = settingsSectionIdFromSlug(slug);
  return id ? settingsLegacyPath(id, search) : `/settings${search}`;
}
